import { useApp } from "../../state/store";
import { useT, type StringKey } from "../../i18n/store";
import { MARGIN_PRESETS } from "../../domain/presets";
import type { MarginPresetKey } from "../../domain/types";
import { Section } from "../Section";
import { NumberInput } from "../NumberInput";
import { Checkbox } from "../Checkbox";

const PRESET_ORDER: MarginPresetKey[] = [
  "tight", "standard", "uniform10", "uniform15", "safe", "us", "custom",
];

const PRESET_KEY_TO_I18N: Record<Exclude<MarginPresetKey, "custom">, StringKey> = {
  tight: "margins.tight",
  standard: "margins.standard",
  uniform10: "margins.uniform10",
  uniform15: "margins.uniform15",
  safe: "margins.safe",
  us: "margins.us",
};

export const MarginsSection = () => {
  const t = useT();
  const margins = useApp((s) => s.margins);
  const setMargins = useApp((s) => s.setMargins);

  const applyPreset = (key: MarginPresetKey) => {
    if (key === "custom") {
      setMargins({ preset: "custom" });
      return;
    }
    const p = MARGIN_PRESETS[key];
    setMargins({
      preset: key,
      top: p.t, right: p.r, bottom: p.b, left: p.l,
      linked: p.linked,
    });
  };

  const setSide = (side: "top" | "right" | "bottom" | "left", val: number) => {
    if (margins.linked) {
      setMargins({ preset: "custom", top: val, right: val, bottom: val, left: val });
    } else {
      setMargins({ preset: "custom", [side]: val });
    }
  };

  return (
    <Section title={t("section.margins")}>
      <div className="row">
        <label>{t("field.preset")}</label>
        <select
          className="ctl"
          value={margins.preset}
          onChange={(e) => applyPreset(e.target.value as MarginPresetKey)}
        >
          {PRESET_ORDER.map((k) => (
            <option key={k} value={k}>
              {k === "custom" ? t("preset.custom") : t(PRESET_KEY_TO_I18N[k])}
            </option>
          ))}
        </select>
      </div>
      <div className="row check">
        <Checkbox
          label={t("margins.linkAll")}
          checked={margins.linked}
          onChange={(linked) => {
            if (linked) {
              const v = margins.top;
              setMargins({ linked, preset: "custom", top: v, right: v, bottom: v, left: v });
            } else {
              setMargins({ linked });
            }
          }}
        />
      </div>
      <div className="margins-grid">
        <div className="mg-cell mg-top">
          <label>{t("margins.top")}</label>
          <NumberInput
            value={margins.top}
            onChange={(v) => setSide("top", v)}
            min={0} max={50} step={0.1}
            unit={t("unit.mm")}
          />
        </div>
        <div className="mg-cell mg-left">
          <label>{t("margins.left")}</label>
          <NumberInput
            value={margins.left}
            onChange={(v) => setSide("left", v)}
            min={0} max={50} step={0.1}
            unit={t("unit.mm")}
          />
        </div>
        <div className="mg-cell mg-right">
          <label>{t("margins.right")}</label>
          <NumberInput
            value={margins.right}
            onChange={(v) => setSide("right", v)}
            min={0} max={50} step={0.1}
            unit={t("unit.mm")}
          />
        </div>
        <div className="mg-cell mg-bottom">
          <label>{t("margins.bottom")}</label>
          <NumberInput
            value={margins.bottom}
            onChange={(v) => setSide("bottom", v)}
            min={0} max={50} step={0.1}
            unit={t("unit.mm")}
          />
        </div>
        <div className="mg-cell mg-center">
          <div className="paper-icon" />
        </div>
      </div>
    </Section>
  );
};
